import { useQueries, useQuery } from '@tanstack/react-query';

import { requestVersionsService } from '../../services/requestVersionsService';

import type {
  RequestVersionDetail,
  RequestVersionFieldHistoryResponse,
  RequestVersionListParams,
  RequestVersionListResponse,
} from '../../types/RequestVersion';

export const requestVersionKeys = {
  all: ['request-versions'] as const,
  list: (requestId: number, params?: RequestVersionListParams) =>
    [...requestVersionKeys.all, 'list', requestId, params ?? {}] as const,
  detail: (requestId: number, versionId: number) =>
    [...requestVersionKeys.all, 'detail', requestId, versionId] as const,
  fieldHistory: (requestId: number, fieldPath: string) =>
    [...requestVersionKeys.all, 'field-history', requestId, fieldPath] as const,
};

/** Paginated version list for a request. */
export const useRequestVersionsQuery = (
  requestId: number | undefined,
  params?: RequestVersionListParams,
) =>
  useQuery<RequestVersionListResponse>({
    queryKey: requestVersionKeys.list(requestId ?? 0, params),
    queryFn: () =>
      requestVersionsService.getVersions(requestId!, {
        ...params,
        pageSize: params?.pageSize ?? 50,
      }),
    enabled: requestId !== undefined,
    // History must reflect the latest saves/restores: always refetch when the
    // screen opens or the tab regains focus rather than serving a stale list.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

/** Full detail (snapshot + delta) for a selected version. */
export const useRequestVersionDetailQuery = (
  requestId: number | undefined,
  versionId: number | undefined,
) =>
  useQuery<RequestVersionDetail>({
    queryKey: requestVersionKeys.detail(requestId ?? 0, versionId ?? 0),
    queryFn: () =>
      requestVersionsService.getVersionDetail(requestId!, versionId!),
    enabled: requestId !== undefined && versionId !== undefined,
  });

/** Full deltas for every listed revision while the field-revision popup is open. */
export const useRequestVersionDetailsQueries = (
  requestId: number | undefined,
  versionIds: number[],
  enabled: boolean,
) =>
  useQueries({
    queries: versionIds.map(versionId => ({
      queryKey: requestVersionKeys.detail(requestId ?? 0, versionId),
      queryFn: () =>
        requestVersionsService.getVersionDetail(requestId!, versionId),
      enabled: enabled && requestId !== undefined,
    })),
  });

/** Changed-only history for one field (Phase 4). Enabled only while a fieldPath is set. */
export const useRequestFieldHistoryQuery = (
  requestId: number | undefined,
  fieldPath: string | undefined,
) =>
  useQuery<RequestVersionFieldHistoryResponse>({
    queryKey: requestVersionKeys.fieldHistory(requestId ?? 0, fieldPath ?? ''),
    queryFn: () =>
      requestVersionsService.getFieldHistory(requestId!, fieldPath!, {
        pageSize: 50,
        orderBy: 'versionNumber',
        orderDirection: 'ASC',
      }),
    enabled: requestId !== undefined && !!fieldPath,
  });
